/**
 * Отмена последнего зафиксированного изменения.
 *
 * Лента снимков, а не полная история с redo: в объём первой версии входит
 * только шаг назад. Фиксируются именно зафиксированные состояния — каждый
 * кадр перетягивания ползунка в ленту не попадает, иначе одно движение мыши
 * съедает всю глубину отмены.
 *
 * Отсюда третье поле. Кадры жеста перерисовывают `present`, и к моменту,
 * когда ползунок отпустили, состояния «до» в ленте уже нет — отмена вернула
 * бы туда, где ученик и так стоит. `anchor` держит состояние до первого
 * кадра жеста; в ленту уходит именно оно.
 */

const DEPTH = 30;

export type History<T> = {
  past: T[];
  present: T;
  /** Состояние до начала жеста, пока жест не закончен. */
  anchor: T | null;
};

export function beginHistory<T>(present: T): History<T> {
  return { past: [], present, anchor: null };
}

/** Промежуточный кадр жеста: видно сразу, шага в ленте пока нет. */
export function preview<T>(history: History<T>, next: T): History<T> {
  if (next === history.present) return history;
  return { past: history.past, present: next, anchor: history.anchor ?? history.present };
}

/** Значение отпущено: в ленту уходит состояние до жеста. */
export function commit<T>(history: History<T>, next: T): History<T> {
  const before = history.anchor ?? history.present;
  if (next === before) return { past: history.past, present: next, anchor: null };
  return { past: [...history.past, before].slice(-DEPTH), present: next, anchor: null };
}

/** Изменение без следа в ленте: панорамирование камеры, выбор активной кривой. */
export function replace<T>(history: History<T>, next: T): History<T> {
  return { past: history.past, present: next, anchor: history.anchor };
}

export function canUndo<T>(history: History<T>): boolean {
  return history.past.length > 0;
}

export function undo<T>(history: History<T>): History<T> {
  const previous = history.past[history.past.length - 1];
  if (previous === undefined) return history;
  return { past: history.past.slice(0, -1), present: previous, anchor: null };
}
